import axios from 'axios'
import router from '@/router'

const api = axios.create({
  baseURL: import.meta.env.BASE_URL || '/',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Store reference to avoid circular dependency
let authStoreInstance = null

// Helper to get auth store
const getAuthStore = async () => {
  if (!authStoreInstance) {
    const { useAuthStore } = await import('@/stores/auth')
    authStoreInstance = useAuthStore()
  }
  return authStoreInstance
}

// Request interceptor - attach JWT token
api.interceptors.request.use(
  async config => {
    const authStore = await getAuthStore()

    // Only a token that is currently valid goes out; an expired or
    // undecodable one would only earn a 401 (SEZ-5044-R05). The `delete`
    // matters on the retry path below, which reuses the same config object
    // and would otherwise carry the header set on the first attempt.
    if (authStore.hasValidAccessToken()) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    } else {
      delete config.headers.Authorization
    }
    return config
  },
  error => Promise.reject(error)
)

// Response interceptor - handle errors
api.interceptors.response.use(
  response => response,
  async error => {
    // A failure thrown by the request interceptor carries no config; without
    // the guard the line below would raise a TypeError and hide the real error.
    const originalRequest = error.config || {}

    // Skip token refresh for auth endpoints (login, register, etc.)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/')

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      const authStore = await getAuthStore()

      // While impersonation tokens are being swapped, let startImpersonation's
      // own error handling restore the admin session — do NOT refresh/logout
      // here (SEZ-4444-R12).
      if (authStore.impersonationInProgress) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        await authStore.refreshAccessToken()
        return api.request(originalRequest)
      } catch (refreshError) {
        // A fault on the way — 5xx, timeout, no response — must not wipe a
        // session the customer may be sharing with a second tab. Only a
        // refusal from the backend ends it (SEZ-5044-R01). Keying this on
        // token expiry alone would leave a revoked-but-unexpired token in a
        // 401 loop with no way back to the login form.
        if (
          !authStore.refreshRejectedByBackend(refreshError) &&
          authStore.hasUsableSession()
        ) {
          return Promise.reject(refreshError)
        }

        // Goes through clearSession, so an operator in impersonation gets
        // their own session back instead of losing it (SEZ-5044-R07).
        authStore.clearSession()

        // Decide on the destination route of the current navigation, not on
        // whatever was on screen before it: a cold start at a public route
        // judged against the previous route brings the defect back
        // intermittently (SEZ-5044-R01).
        //
        // Read without awaiting `router.isReady()`. This request may BE the
        // one the initial navigation is parked on inside the guard, in which
        // case the router can never become ready and the app would hang on a
        // blank page. Until the first navigation settles, `currentRoute` has
        // no match, so resolve the address bar instead.
        const current = router.currentRoute.value
        const destination = current.matched.length
          ? current
          : router.resolve(window.location.pathname + window.location.search)

        if (destination.meta.requiresAuth && !authStore.hasUsableSession()) {
          router.push({
            path: '/login',
            query: { redirect: destination.fullPath }
          })
        }

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default api
